/**
 * Fleet Data Aggregation Hook
 *
 * Aggregates compliance data across all vessels for Fleet Dashboard.
 * Read-only aggregation layer - does NOT modify individual vessel data.
 *
 * Provides:
 * - RAG status per vessel (Red/Amber/Green)
 * - Fleet-wide compliance metrics
 * - Certificate expiry tracking
 * - Drill compliance summary
 * - Defect overview
 *
 * @see FLEET-DASHBOARD-DESIGN.md for full specification
 */

import { useCallback, useMemo, useState, useEffect } from 'react';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
import { filterTombstoned } from '../../../sync/tombstone';  // FT-458: exclude soft-deleted defects/drills/maintenance from RAG + fleet counts

/**
 * Calculate RAG status for a vessel based on compliance data
 */
const calculateRAGStatus = (complianceData) => {
  const {
    expiredCerts = 0,
    criticalDefects = 0,
    overdueItems = 0,
    expiringCerts = 0,
    dueSoonItems = 0,
    openDefects = 0
  } = complianceData;

  // RED: Overdue items OR critical defects OR expired certificates
  if (expiredCerts > 0 || criticalDefects > 0 || overdueItems > 0) {
    return 'red';
  }

  // AMBER: Items due within 7 days OR non-critical issues
  if (expiringCerts > 0 || dueSoonItems > 0 || openDefects > 0) {
    return 'amber';
  }

  // GREEN: All compliant
  return 'green';
};

/**
 * Read vessel data from localStorage
 */
const readVesselData = (vesselId, key) => {
  try {
    const storageKey = `${key}_${vesselId}`;
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.warn(`Error reading ${key} for vessel ${vesselId}:`, e);
    return null;
  }
};

/**
 * Hook for aggregating fleet-wide data
 *
 * @returns {Object} Fleet data and metrics
 */
export const useFleetData = () => {
  const { vessels, isLoading: vesselsLoading } = useVessel();
  const { isFleet } = usePermissions();

  const [fleetData, setFleetData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  /**
   * Aggregate data for a single vessel
   */
  const aggregateVesselData = useCallback((vessel) => {
    const vesselId = vessel.id;
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    // Read various data sources
    // FT-458: soft-deleted (tombstoned) records must NOT inflate RAG status or
    // fleet counts — a deleted critical defect must not keep a vessel RED, and a
    // deleted drill/maintenance record must not count. Filter at the read site
    // before any .filter/.length/count downstream.
    const defects = filterTombstoned(readVesselData(vesselId, 'defects') || []);
    const drills = filterTombstoned(readVesselData(vesselId, 'drills') || []);
    const crew = readVesselData(vesselId, 'crew') || [];
    const equipment = readVesselData(vesselId, 'equipment_register') || [];
    const maintenanceHistory = filterTombstoned(readVesselData(vesselId, 'maintenance_history') || []);
    const vesselSettings = readVesselData(vesselId, 'vesselSettings') || {};

    // Calculate defect metrics
    const openDefects = defects.filter(d => d.status !== 'closed' && d.status !== 'resolved');
    const criticalDefects = openDefects.filter(d =>
      d.priority === 'critical' || d.severity === 'critical'
    );

    // Calculate drill compliance
    const recentDrills = drills.filter(d => {
      const drillDate = new Date(d.date || d.completedAt);
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return drillDate >= thirtyDaysAgo;
    });

    // Check for overdue statutory drills (simplified - would need drill schedule config)
    const overdueDrills = 0; // TODO: Calculate from drill schedule

    // Calculate certificate expiry (crew certificates)
    const crewCerts = crew.flatMap(c => c.certificates || []);
    const expiredCerts = crewCerts.filter(cert => {
      if (!cert.expiryDate) return false;
      return new Date(cert.expiryDate) < now;
    });
    const expiringCerts = crewCerts.filter(cert => {
      if (!cert.expiryDate) return false;
      const expiry = new Date(cert.expiryDate);
      return expiry >= now && expiry <= thirtyDaysFromNow;
    });

    // Calculate maintenance metrics
    const overdueJobs = maintenanceHistory.filter(job => {
      if (job.status === 'completed') return false;
      if (!job.dueDate) return false;
      return new Date(job.dueDate) < now;
    });

    const dueSoonJobs = maintenanceHistory.filter(job => {
      if (job.status === 'completed') return false;
      if (!job.dueDate) return false;
      const dueDate = new Date(job.dueDate);
      return dueDate >= now && dueDate <= sevenDaysFromNow;
    });

    // Calculate equipment status
    const outOfServiceEquipment = equipment.filter(eq =>
      eq.status === 'out_of_service' || eq.status === 'defective'
    );

    // Compile compliance data
    const complianceData = {
      expiredCerts: expiredCerts.length,
      expiringCerts: expiringCerts.length,
      criticalDefects: criticalDefects.length,
      openDefects: openDefects.length,
      overdueItems: overdueJobs.length + overdueDrills,
      dueSoonItems: dueSoonJobs.length,
      outOfServiceEquipment: outOfServiceEquipment.length
    };

    // Calculate RAG status
    const ragStatus = calculateRAGStatus(complianceData);

    return {
      vessel,
      vesselId,
      vesselName: vessel.name,
      ragStatus,
      compliance: complianceData,
      summary: {
        certificates: {
          expired: expiredCerts.length,
          expiring: expiringCerts.length,
          status: expiredCerts.length > 0 ? 'red' : expiringCerts.length > 0 ? 'amber' : 'green'
        },
        drills: {
          recent: recentDrills.length,
          overdue: overdueDrills,
          status: overdueDrills > 0 ? 'red' : 'green'
        },
        defects: {
          open: openDefects.length,
          critical: criticalDefects.length,
          status: criticalDefects.length > 0 ? 'red' : openDefects.length > 0 ? 'amber' : 'green'
        },
        crew: {
          total: crew.length,
          certsExpiring: expiringCerts.length,
          status: expiredCerts.length > 0 ? 'red' : expiringCerts.length > 0 ? 'amber' : 'green'
        },
        maintenance: {
          overdue: overdueJobs.length,
          dueSoon: dueSoonJobs.length,
          status: overdueJobs.length > 0 ? 'red' : dueSoonJobs.length > 0 ? 'amber' : 'green'
        },
        equipment: {
          outOfService: outOfServiceEquipment.length,
          total: equipment.length,
          status: outOfServiceEquipment.length > 0 ? 'amber' : 'green'
        }
      },
      lastActivity: null, // TODO: Track from audit log
      updatedAt: new Date().toISOString()
    };
  }, []);

  /**
   * Refresh all fleet data
   */
  const refreshFleetData = useCallback(() => {
    if (!isFleet || !vessels || vessels.length === 0) {
      setFleetData({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const data = {};
    vessels.forEach(vessel => {
      data[vessel.id] = aggregateVesselData(vessel);
    });

    setFleetData(data);
    setLastRefresh(new Date().toISOString());
    setIsLoading(false);
  }, [isFleet, vessels, aggregateVesselData]);

  // Refresh on mount and when vessels change
  useEffect(() => {
    if (!vesselsLoading) {
      refreshFleetData();
    }
  }, [vesselsLoading, refreshFleetData]);

  // Listen for storage changes and refresh
  useEffect(() => {
    const handleStorageChange = () => {
      refreshFleetData();
    };

    window.addEventListener('vesselStorageUpdated', handleStorageChange);
    window.addEventListener('sr:storage:set', handleStorageChange);

    return () => {
      window.removeEventListener('vesselStorageUpdated', handleStorageChange);
      window.removeEventListener('sr:storage:set', handleStorageChange);
    };
  }, [refreshFleetData]);

  /**
   * Get fleet-wide summary metrics
   */
  const fleetSummary = useMemo(() => {
    const vesselDataList = Object.values(fleetData);

    if (vesselDataList.length === 0) {
      return {
        totalVessels: 0,
        compliant: 0,
        attention: 0,
        actionRequired: 0,
        totalExpiredCerts: 0,
        totalExpiringCerts: 0,
        totalCriticalDefects: 0,
        totalOpenDefects: 0,
        totalOverdueItems: 0
      };
    }

    return {
      totalVessels: vesselDataList.length,
      compliant: vesselDataList.filter(v => v.ragStatus === 'green').length,
      attention: vesselDataList.filter(v => v.ragStatus === 'amber').length,
      actionRequired: vesselDataList.filter(v => v.ragStatus === 'red').length,
      totalExpiredCerts: vesselDataList.reduce((sum, v) => sum + v.compliance.expiredCerts, 0),
      totalExpiringCerts: vesselDataList.reduce((sum, v) => sum + v.compliance.expiringCerts, 0),
      totalCriticalDefects: vesselDataList.reduce((sum, v) => sum + v.compliance.criticalDefects, 0),
      totalOpenDefects: vesselDataList.reduce((sum, v) => sum + v.compliance.openDefects, 0),
      totalOverdueItems: vesselDataList.reduce((sum, v) => sum + v.compliance.overdueItems, 0)
    };
  }, [fleetData]);

  /**
   * Get vessels sorted by status (red first, then amber, then green)
   */
  const vesselsByStatus = useMemo(() => {
    const vesselDataList = Object.values(fleetData);

    const statusOrder = { red: 0, amber: 1, green: 2 };

    return vesselDataList.sort((a, b) =>
      statusOrder[a.ragStatus] - statusOrder[b.ragStatus]
    );
  }, [fleetData]);

  /**
   * Get all expiring certificates across fleet
   */
  const getExpiringCertificates = useCallback((daysAhead = 90) => {
    const certificates = [];
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() + daysAhead);

    vessels.forEach(vessel => {
      const crew = readVesselData(vessel.id, 'crew') || [];

      crew.forEach(crewMember => {
        (crewMember.certificates || []).forEach(cert => {
          if (!cert.expiryDate) return;

          const expiry = new Date(cert.expiryDate);
          if (expiry <= cutoffDate) {
            certificates.push({
              vesselId: vessel.id,
              vesselName: vessel.name,
              crewMemberId: crewMember.id,
              crewMemberName: crewMember.name,
              certificateType: cert.type || cert.name,
              expiryDate: cert.expiryDate,
              isExpired: expiry < new Date(),
              daysUntilExpiry: Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24))
            });
          }
        });
      });
    });

    return certificates.sort((a, b) =>
      new Date(a.expiryDate) - new Date(b.expiryDate)
    );
  }, [vessels]);

  /**
   * Get all critical defects across fleet
   */
  const getCriticalDefects = useCallback(() => {
    const criticalDefects = [];

    vessels.forEach(vessel => {
      // FT-458: exclude soft-deleted defects from the fleet-wide critical list.
      const defects = filterTombstoned(readVesselData(vessel.id, 'defects') || []);

      defects
        .filter(d =>
          (d.priority === 'critical' || d.severity === 'critical') &&
          d.status !== 'closed' &&
          d.status !== 'resolved'
        )
        .forEach(defect => {
          criticalDefects.push({
            ...defect,
            vesselId: vessel.id,
            vesselName: vessel.name
          });
        });
    });

    return criticalDefects.sort((a, b) =>
      new Date(b.reportedAt || b.createdAt) - new Date(a.reportedAt || a.createdAt)
    );
  }, [vessels]);

  return {
    // Loading state
    isLoading: isLoading || vesselsLoading,
    lastRefresh,

    // Fleet data
    fleetData,
    fleetSummary,
    vesselsByStatus,

    // Actions
    refreshFleetData,

    // Queries
    getExpiringCertificates,
    getCriticalDefects,

    // Helper to get single vessel data
    getVesselData: (vesselId) => fleetData[vesselId] || null
  };
};

export default useFleetData;
